import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useParams } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { DatabaseBadge } from "../../components/DatabaseBadge";
import { DetailHeader } from "../../components/DetailHeader";
import { Alert, Button, LoadingBlock } from "../../components/ui";
import { describeEngine } from "../../lib/engines";
import { regionLabel } from "../../lib/regions";
import { vanishedWhileCreating } from "../../lib/vanished";
import { formatPrice } from "../../components/PlanPicker";
import {
  useAccessRules,
  useBackupSchedule,
  useDatabase,
  useDatabaseLifecycle,
} from "../../lib/queries";

/*
 * The same header apps wear, for the same reason: a customer moving between
 * the two should not have to re-learn where the name, the state, the address
 * and the actions live.
 *
 * Access keeps its name because the docs send people to it by that name.
 * Deleting lives there too rather than in a tab of its own: it is admin-only,
 * and so is everything else on that tab.
 */
/*
 * A header fact that is also the way to the tab that owns it. The value keeps
 * the foreground colour — these are facts first — and answers hover with the
 * accent, which is the site-wide grammar for "this goes somewhere".
 */
function FactLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 transition-colors duration-200 ease-smooth hover:text-accent"
    >
      {children}
    </Link>
  );
}

/* Route and key per tab; the words are in `database.json` under `tabs`. */
const TABS = [
  { to: ".", key: "overview", end: true },
  { to: "logs", key: "logs" },
  { to: "backups", key: "backups" },
  { to: "access", key: "access" },
] as const;

export function DatabaseDetailPage() {
  const { t } = useTranslation("database");
  const { databaseId } = useParams();
  const { currentOrg } = useAuth();
  const canManage = useCan("developer");

  const { data: database, isPending, error } = useDatabase(currentOrg?.id, databaseId);
  const lifecycle = useDatabaseLifecycle(currentOrg?.id ?? "");
  /*
   * The header states what the tabs own — backups on or off, the allowlist —
   * so the page's standing is readable without opening anything. Both queries
   * share their tab's cache key, so this costs no second request once a tab
   * has loaded, and the allowlist read is skipped entirely while the port is
   * private.
   */
  const backups = useBackupSchedule(currentOrg?.id, databaseId ?? null);
  const accessRules = useAccessRules(
    currentOrg?.id,
    database?.externalPort != null ? database.id : undefined,
  );
  if (isPending) return <LoadingBlock />;
  if (error) {
    // The poll that watches "creating" can find the row gone: the API removes
    // a create that never finished. Say so, rather than "not found".
    const vanished = vanishedWhileCreating(error, "database", database?.status);
    return (
      <Alert>
        {vanished ?? error.message}
        {vanished && (
          <>
            {" "}
            <Link to="/databases" className="underline">
              {t("backLink")}
            </Link>
          </>
        )}
      </Alert>
    );
  }
  if (!database) return <Alert>{t("notFound")}</Alert>;

  const state = database.status.value;
  const settling = state === "creating";
  const canAct = canManage && !settling && !database.status.stale;
  /*
   * Not a link: this hostname resolves only inside the project's network, so
   * an anchor would offer a click that cannot work from a browser. Copyable,
   * because pasting it into an app's configuration is exactly what it is for.
   */
  const host = database.internalHost ? `${database.internalHost}:${database.port}` : "";

  return (
    <div className="space-y-6">
      <DetailHeader
        backTo="/databases"
        backLabel={t("back")}
        name={database.name}
        badge={<DatabaseBadge state={database.status.value} stale={database.status.stale} />}
        address={{
          value: host,
          copyLabel: t("copyAddress"),
          empty: <span className="text-sm text-fg-subtle">{t("noAddress")}</span>,
        }}
        actions={
          canAct && (
            <Button
              variant="secondary"
              loading={lifecycle.isPending}
              onClick={() =>
                lifecycle.mutate({ id: database.id, action: state === "running" ? "stop" : "start" })
              }
            >
              {state === "running" ? t("stop") : t("start")}
            </Button>
          )
        }
        meta={[
          { label: t("facts.engine"), value: describeEngine(database.engine) },
          // Where it runs. A fact with no control: a database cannot move.
          { label: t("facts.region"), value: regionLabel(database.region) ?? "—" },
          /*
           * Each fact links to the tab that owns it, so the header is a map
           * of the page rather than a dead end — the value is readable here,
           * and the control it belongs to is one click along the same line.
           * The plan's figure stays beside its change control on Overview;
           * this entry is the pointer to it.
           */
          ...(database.plan
            ? [
                {
                  label: t("facts.plan"),
                  value: (
                    <FactLink to=".">
                      {database.plan.name}
                      <span className="text-fg-subtle">
                        {formatPrice(database.plan.price.cents, database.plan.price.currency)}/mo
                      </span>
                    </FactLink>
                  ),
                },
              ]
            : []),
          {
            label: t("facts.exposure"),
            value: (
              <FactLink to="access">
                {database.externalPort
                  ? t("facts.published", { port: database.externalPort })
                  : t("facts.private")}
              </FactLink>
            ),
          },
          ...(database.externalPort != null && accessRules.data
            ? [
                {
                  label: t("facts.allowed"),
                  value: (
                    <FactLink to="access">
                      {accessRules.data.data.length === 0
                        ? t("facts.openToAnyone")
                        : t("facts.networks", { count: accessRules.data.data.length })}
                    </FactLink>
                  ),
                },
              ]
            : []),
          ...(backups.data
            ? [
                {
                  label: t("facts.backups"),
                  value: (
                    <FactLink to="backups">
                      {!backups.data.supported
                        ? t("facts.backupsUnavailable")
                        : backups.data.data?.enabled
                          ? t("facts.backupsOn", { frequency: backups.data.data.frequency })
                          : t("facts.backupsOff")}
                    </FactLink>
                  ),
                },
              ]
            : []),
        ]}
        tabs={TABS.map((tab) => ({ ...tab, label: t(`tabs.${tab.key}`) }))}
      />

      {lifecycle.error && <Alert>{lifecycle.error.message}</Alert>}

      {settling && (
        <Alert tone="info">{t("settling")}</Alert>
      )}

      <Outlet context={{ orgId: currentOrg?.id, database }} />
    </div>
  );
}
